import React, { useState, useRef, useImperativeHandle, forwardRef, useEffect } from 'react';
import { Send, Square } from 'lucide-react';
import { useJobs } from '../../../contexts/JobContext';
import styles from './ChatInput.module.css';
import logger from '../../../logging/logger';

interface ChatInputProps {
  onSend: (message: string) => void;
  onStop?: () => void;
  isBottom: boolean;
  isProcessing?: boolean;
  isFirstMessage?: boolean;
  isSessionBusy?: boolean;
}

export interface ChatInputRef {
  focus: () => void;
}

export const ChatInput = forwardRef<ChatInputRef, ChatInputProps>(({ onSend, onStop, isBottom, isProcessing = false, isFirstMessage = false, isSessionBusy = false }, ref) => {
  const [message, setMessage] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { activeJobs, cancelJob } = useJobs();

  // Check if there's an active backtest job
  // By accessing activeJobs directly, the component will re-render when it changes
  const activeBacktestJob = activeJobs.find(job => job.type === 'backtest');

  // Debug logging
  logger.info('🔍 [ChatInput] Render - Active jobs:', activeJobs.length, 'Backtest job:', activeBacktestJob);

  // Array of witty messages for session busy state
  const busyMessages = [
    "Another chat is active in a different tab. Please continue there first.",
    "This session is currently busy in another tab. Finish that response before sending here.",
    "Only one active conversation is allowed at a time for this session.",
    "Please wait for the other tab to finish before sending a new message here."
  ];

  // Get a consistent witty message (same message for the same session)
  const getBusyMessage = () => {
    // Use a simple hash of the current time to get a pseudo-random but consistent message
    // This ensures the same user sees the same message during their session
    const now = new Date();
    const seed = now.getHours() + now.getMinutes(); // Changes every minute
    const messageIndex = seed % busyMessages.length;
    return busyMessages[messageIndex];
  };

  // Expose focus method to parent components
  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
    }
  }));

  // Auto-focus when agent completes response (isProcessing changes from true to false)
  useEffect(() => {
    if (!isProcessing && inputRef.current) {
      // Small delay to ensure the UI has updated
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isProcessing]);

  // Auto-resize textarea based on content
  useEffect(() => {
    const textarea = inputRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set height based on scrollHeight, with min and max limits
      const newHeight = Math.min(Math.max(textarea.scrollHeight, 44), 300); // Min 44px, Max 300px
      textarea.style.height = `${newHeight}px`;
    }
  }, [message]);

  const handleSend = () => {
    if (message.trim() && !isProcessing) {
      onSend(message);
      setMessage('');
    }
  };

  const handleStop = async () => {
    logger.info('🛑 Stop button clicked');

    // If there's an active backtest job, cancel it
    if (activeBacktestJob) {
      logger.info('🛑 Cancelling backtest job:', activeBacktestJob.jobId);
      const cancelled = await cancelJob(activeBacktestJob.jobId);
      if (cancelled) {
        logger.info('✅ Backtest job cancelled successfully');
      } else {
        logger.error('❌ Failed to cancel backtest job');
      }
    }

    // Also call the onStop callback if provided (for AI generation)
    if (onStop) {
      logger.info('🛑 Calling onStop function for AI generation');
      onStop();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !isProcessing && !activeBacktestJob) {
      e.preventDefault();
      handleSend();
    }
  };

  // Show stop button if either AI is processing OR there's an active backtest
  const shouldShowStop = isProcessing || !!activeBacktestJob;
  const isInputDisabled = isProcessing || isSessionBusy || !!activeBacktestJob;

  return (
    <div className={`${styles.container} ${isBottom ? styles.bottom : ''}`}>
      <textarea
        ref={inputRef}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={
          isSessionBusy
            ? getBusyMessage()
            : activeBacktestJob
            ? `Running backtest: ${activeBacktestJob.description || 'Backtest in progress'}...`
            : isProcessing
            ? "Processing..."
            : isFirstMessage
            ? "Ask about a strategy, backtest, or market idea..."
            : "Type your message..."
        }
        className={styles.input}
        disabled={isInputDisabled}
        rows={1}
      />
      <button
        onClick={shouldShowStop ? handleStop : handleSend}
        className={`${styles.button} ${shouldShowStop ? styles.stopButton : ''}`}
        disabled={shouldShowStop ? false : (!message.trim() || isSessionBusy)}
        title={activeBacktestJob ? 'Stop backtest' : isProcessing ? 'Stop generation' : 'Send message'}
      >
        {shouldShowStop ? <Square size={16} /> : <Send size={16} />}
      </button>
    </div>
  );
});

ChatInput.displayName = 'ChatInput';
